import { JackpotMode, JackpotSmallSuitAnimationEvent } from '@amusnet/react-ui'
import {
    defaultJackpotCardsStatistics,
    defaultJackpotLinkStatistics,
    JackpotIndexSuitMap,
    JackpotLinkTypes,
    JackpotPhase,
    JackpotSuits,
    JackpotType,
    JackpotCardsStatistics,
    JackpotLinkStatistics,
    JackpotLinkStonesType,
    JackpotEQStatistics,
    JackpotCardsGameState,
    JackpotEQGameState,
    defaultJackpotEQStatistics,
    JackpotEQLevels,
} from 'game-ui-types'
import { create } from 'zustand'
import { useNavigationStore } from './navigationStore'

export interface JackpotStoreState {
    jackpotStatsAnimationDurationSeconds: number
    jackpotStatsMaxOdometerLimit: { [key in JackpotSuits | JackpotLinkTypes | JackpotEQLevels]: number }
    isJackpotSettingActive: boolean
    setIsJackpotSettingActive: (isActive: boolean) => void
    jackpotType: JackpotType
    setJackpotType: (type: JackpotType) => void
    jackpotGameState: JackpotCardsGameState | JackpotEQGameState
    isEQJackpotType: boolean
    setJackpotGameState: (state: JackpotCardsGameState | JackpotEQGameState) => void
    jackpotPhase: JackpotPhase
    isJackpotStarted: boolean
    isJackpotRoundRunning: boolean
    setJackpotPhase: (jackpotPhase: JackpotPhase) => void
    isJackpotLinkHoldSpin: boolean
    setIsJackpotLinkHoldSpin: (isJackpotLinkHoldSpin: boolean) => void
    isJackpotEQStarted: boolean
    setIsJackpotEQStarted: (isJackpotEQStarted: boolean) => void
    toolTipDateSeparator: string
    setToolTipDateSeparator: (separator: string) => void
    currencyHideCentsOnlyInJackpotBoxes: boolean
    setCurrencyHideCentsOnlyInJackpotBoxes: (hide: boolean) => void
    jackpotEQStatistics: JackpotEQStatistics
    setJackpotEQStatistics: (stats: JackpotEQStatistics) => void
    jackpotCardsStatistics: JackpotCardsStatistics
    setJackpotCardsStatistics: (stats: JackpotCardsStatistics) => void
    jackpotLinkStatistics: JackpotLinkStatistics
    setJackpotLinkStatistics: (stats: JackpotLinkStatistics) => void
    pickedSuits: { [key in JackpotSuits]: number }
    incrementPickedSuit: (suit: JackpotSuits) => void
    resetPickedSuits: () => void
    pickedStones: { [key in JackpotLinkStonesType]: number }
    incrementPickedStones: (type: JackpotLinkStonesType) => void
    resetPickedStones: () => void
    freezeIndex: number | -1
    setFreezeIndex: (index: number | -1) => void
    jackpotStatsCountdownToZeroDurationSeconds: number
    setJackpotStatsCountdownToZeroDurationSeconds: (duration: number) => void
    hasLeadElement: boolean
    setHasLeadElement: (hasElement: boolean) => void
    // Amusnet react ui jackpot component related states
    isJackpotWon: boolean
    setIsJackpotWon: (isJackpotWon: boolean) => void
    latestSuitEvent: JackpotSmallSuitAnimationEvent | null
    setLatestSuitEvent: (event: JackpotSmallSuitAnimationEvent | null) => void
    mode: JackpotMode
    setMode: (mode: JackpotMode) => void
    winningLevelIndex?: number
    setWinningLevelIndex: (index: number | undefined) => void
    isHistoryEnabled: boolean
    setIsHistoryEnabled: (isHistoryEnabled: boolean) => void
    closeHistorySignal: number
    closeJackpotHistory: () => void
}

export const useJackpotStore = create<JackpotStoreState>((set) => ({
    jackpotStatsAnimationDurationSeconds: 5,
    jackpotStatsMaxOdometerLimit: {
        [JackpotEQLevels.BRONZE]: 100,
        [JackpotEQLevels.SILVER]: 100,
        [JackpotEQLevels.GOLD]: 100,
        [JackpotEQLevels.PLATINUM]: 100,
        [JackpotSuits.CLUBS]: 100,
        [JackpotSuits.DIAMONDS]: 100,
        [JackpotSuits.HEARTS]: 100,
        [JackpotSuits.SPADES]: 100,
        [JackpotLinkTypes.GRAND]: 0,
        [JackpotLinkTypes.MAJOR]: 0,
        [JackpotLinkTypes.MINOR]: 0,
        [JackpotLinkTypes.MINI]: 0,
    },

    isJackpotSettingActive: false,
    setIsJackpotSettingActive: (isActive: boolean) => {
        set({ isJackpotSettingActive: isActive })
    },

    isEQJackpotType: false,

    jackpotType: JackpotType.JACKPOT_CARDS,
    setJackpotType: (jackpotType: JackpotType) => {
        set({ jackpotType, isEQJackpotType: jackpotType === JackpotType.JACKPOT_EGYPT_QUEST })
    },
    jackpotGameState: {},
    setJackpotGameState: (state: JackpotCardsGameState | JackpotEQGameState) => {
        // Suits in Remote jackpot returns 0, 1, 2, 3. Local jackpot returns 11, 24, 37, 50
        // Should receive in this format: 0, 1, 2, 3
        const allPickedSuits = Object.values(state).map((n) => JackpotIndexSuitMap[n])
        const pickedSuitsMap = allPickedSuits.reduce(
            (acc, current) => {
                acc[current] = acc[current] + 1

                return acc
            },
            {
                [JackpotSuits.CLUBS]: 0,
                [JackpotSuits.DIAMONDS]: 0,
                [JackpotSuits.HEARTS]: 0,
                [JackpotSuits.SPADES]: 0,
            }
        )

        set({ jackpotGameState: state, pickedSuits: pickedSuitsMap })
    },

    jackpotPhase: JackpotPhase.CLOSED,
    isJackpotStarted: false,
    isJackpotRoundRunning: false,
    setJackpotPhase: (jackpotPhase: JackpotPhase) => {
        set({
            jackpotPhase,
            isJackpotStarted: jackpotPhase !== JackpotPhase.CLOSED && jackpotPhase !== JackpotPhase.PENDING,
            isJackpotRoundRunning: jackpotPhase === JackpotPhase.RUNNING,
        })
    },

    isJackpotLinkHoldSpin: false,
    setIsJackpotLinkHoldSpin: (isJackpotLinkHoldSpin: boolean) => {
        set({ isJackpotLinkHoldSpin })
    },

    isJackpotEQStarted: false,
    setIsJackpotEQStarted: (isJackpotEQStarted: boolean) => {
        set({ isJackpotEQStarted })
    },

    toolTipDateSeparator: '',
    setToolTipDateSeparator: (separator: string) => {
        set({ toolTipDateSeparator: separator })
    },

    currencyHideCentsOnlyInJackpotBoxes: false,
    setCurrencyHideCentsOnlyInJackpotBoxes: (hide: boolean) => {
        set({ currencyHideCentsOnlyInJackpotBoxes: hide })
    },

    jackpotCardsStatistics: defaultJackpotCardsStatistics,
    setJackpotCardsStatistics: (stats: JackpotCardsStatistics) => {
        set((state) => ({
            jackpotCardsStatistics: {
                ...state.jackpotCardsStatistics,
                // these props are not always sent, reset them otherwise it looks like someone is winning nonstop.
                winLevel: -1,
                winAmount: -1,
                screenName: '',
                ...stats,
            },
        }))
    },

    jackpotEQStatistics: defaultJackpotEQStatistics,
    setJackpotEQStatistics: (stats: JackpotEQStatistics) => {
        set((state) => ({
            jackpotEQStatistics: {
                ...state.jackpotEQStatistics,
                // these props are not always sent, reset them otherwise it looks like someone is winning nonstop.
                winLevel: -1,
                winAmount: -1,
                screenName: '',
                ...stats,
            },
        }))
    },

    jackpotLinkStatistics: defaultJackpotLinkStatistics,
    setJackpotLinkStatistics: (stats: JackpotLinkStatistics) => {
        set({ jackpotLinkStatistics: stats })
    },

    pickedSuits: {
        [JackpotSuits.CLUBS]: 0,
        [JackpotSuits.DIAMONDS]: 0,
        [JackpotSuits.HEARTS]: 0,
        [JackpotSuits.SPADES]: 0,
    },
    incrementPickedSuit: (suit: JackpotSuits) => {
        set((state) => ({
            pickedSuits: {
                ...state.pickedSuits,
                [suit]: state.pickedSuits[suit] + 1,
            },
        }))
    },
    resetPickedSuits: () => {
        set({
            pickedSuits: {
                [JackpotSuits.CLUBS]: 0,
                [JackpotSuits.DIAMONDS]: 0,
                [JackpotSuits.HEARTS]: 0,
                [JackpotSuits.SPADES]: 0,
            },
        })
    },

    pickedStones: {
        [JackpotLinkTypes.GRAND]: 0,
        [JackpotLinkTypes.MAJOR]: 0,
    },
    incrementPickedStones: (type: JackpotLinkStonesType) => {
        set((state) => ({
            pickedStones: {
                ...state.pickedStones,
                [type]: state.pickedStones[type] + 1,
            },
        }))
    },
    resetPickedStones: () => {
        set({
            pickedStones: {
                [JackpotLinkTypes.GRAND]: 0,
                [JackpotLinkTypes.MAJOR]: 0,
            },
        })
    },

    freezeIndex: -1,
    setFreezeIndex: (index: number | -1) => {
        // When index populated the relative jackpot stats item has been won by the current player
        // so that during jackpot results the statistics can animate the win
        // Set to -1 to reset
        // Index is JackpotIndexSuitMap or JackpotLinkIndexTypeMap
        set({ freezeIndex: index })
    },

    jackpotStatsCountdownToZeroDurationSeconds: 4,
    setJackpotStatsCountdownToZeroDurationSeconds: (duration: number) => {
        set({ jackpotStatsCountdownToZeroDurationSeconds: duration })
    },

    hasLeadElement: false,
    setHasLeadElement: (hasElement = true) => {
        set({ hasLeadElement: hasElement })
    },
    isJackpotWon: false,
    setIsJackpotWon: (isJackpotWon: boolean) => {
        set({ isJackpotWon })
    },
    latestSuitEvent: null,
    setLatestSuitEvent: (event: JackpotSmallSuitAnimationEvent | null) => {
        set({ latestSuitEvent: event })
    },
    mode: JackpotMode.IDLE,
    setMode: (mode: JackpotMode) => {
        set({ mode })
    },
    winningLevelIndex: undefined,
    setWinningLevelIndex: (index: number | undefined) => {
        set({ winningLevelIndex: index })
    },

    isHistoryEnabled: true,
    setIsHistoryEnabled: (isHistoryEnabled: boolean) => {
        set({ isHistoryEnabled })
    },

    closeHistorySignal: 0,
    closeJackpotHistory: () => {
        set((state) => ({ closeHistorySignal: state.closeHistorySignal + 1 }))
    },
}))

// Jackpot history targets same behavior as settings menu in terms of disabling/enabling
useNavigationStore.subscribe((state, prevState) => {
    if (state.isMenuDisabled !== prevState.isMenuDisabled) {
        useJackpotStore.getState().setIsHistoryEnabled(!state.isMenuDisabled)
    }
})
